import {
	Box,
	Button,
	Card,
	CardActions,
	Grid,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
	useEditMembershipMutation,
	useGetAdmissionRequestDetailQuery,
} from '@/api/member-management/admissionRequest';
import { YESNO, YESNOLIST } from '@/common/constants/common';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import AppAutoCompleteMenu from '@/common/components/form-inputs/AppAutoCompleteMenu';
import { useGetAllEnumListQuery } from '@/api/common';

interface Props {
	type: string;
}
export default function EditMembershipInformation(props: Props) {
	const { type } = props;
	const memberType = i18n.t(
		type === 'admissionRequest'
			? 'member.admissionRequestLabel'
			: 'member.memberDetailsLabel'
	);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { admissionRequestId } = useParams();
	const [editMembership] = useEditMembershipMutation();

	const { data: details } = useGetAdmissionRequestDetailQuery(
		admissionRequestId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const { data: allEnumList } = useGetAllEnumListQuery(null);
	const toEnumOptions = (list?: { code: number; desc: string }[]) =>
		(list || []).map((item) => ({ id: item.code, name: item.desc }));
	const toNullableCode = (value: any) =>
		value === null || value === undefined || value === ''
			? null
			: Number(value);

	const defaultValues: DefaultValues<any> = {
		mailRequest: null,
		newsletterConfidential: null,
		contactMethod: null,
		homeVisit: null,
		inactiveMember: null,
	};

	const formSchema = Yup.object().shape({
		mailRequest: Yup.string().required(t('errorMessage.pleaseSelect')),
		newsletterConfidential: Yup.string().required(
			t('errorMessage.pleaseSelect')
		),
		inactiveMember: Yup.string().required(t('errorMessage.pleaseSelect')),
	});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
		mode: 'onChange',
		shouldFocusError: true,
	});

	// The API sends these back as true/false/null and takes booleans in return,
	// but the radio hands its value back as a string — normalise both ways, and
	// keep null as "not answered" so an unset field stays blank.
	const toYesNoValue = (value: any) => {
		if (value === null || value === undefined || value === '') return null;
		if (typeof value === 'string') return value === 'true' || value === '1';
		return Boolean(value);
	};

	// YESNOLIST carries numeric ids, so a boolean from the API has to be
	// mapped to 1/0 — but null stays null, or an unanswered field pre-fills as No.
	const toOneZeroValue = (value: any) =>
		value === null || value === undefined ? null : value ? 1 : 0;

	useEffect(() => {
		if (details) {
			methods.reset(details.data);
			methods.setValue('mailRequest', toYesNoValue(details.data.mailRequest));
			methods.setValue(
				'newsletterConfidential',
				toYesNoValue(details.data.newsletterConfidential)
			);
			methods.setValue(
				'inactiveMember',
				toOneZeroValue(details.data.inactiveMember)
			);
		}
	}, [details]);

	const onSubmit: SubmitHandler<any> = async (data) => {
		const obj: any = {
			...data,
			mailRequest: toYesNoValue(data.mailRequest),
			newsletterConfidential: toYesNoValue(data.newsletterConfidential),
			contactMethod: toNullableCode(data.contactMethod),
			homeVisit: toNullableCode(data.homeVisit),
		};
		const response = await editMembership(obj);

		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateMemberSuccess', { type: memberType }), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			navigate(
				`/member-management/${
					type == 'member' ? 'member-list' : 'admission-request-list'
				}/detail/${admissionRequestId}`
			);
		}
	};

	const handleClose = () => {
		navigate(
			`/member-management/${
				type == 'member' ? 'member-list' : 'admission-request-list'
			}/detail/${admissionRequestId}`,
			{ replace: true }
		);
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit(onSubmit)}>
						<Box display='flex' justifyContent='space-between'>
							<Typography variant='h4'>
								{type == 'admissionRequest'
									? t('drawerListItems.editAdmissionRequest')
									: t('drawerListItems.editMember')}
							</Typography>
						</Box>
						<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
							<Box
								display='flex'
								justifyContent='space-between'
								marginBottom={2}
							>
								<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
									{t('member.membershipInformation')}
								</Typography>
							</Box>
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid size={6}>
									<Typography variant='body2' color='common.neutral40'>
										{t('member.mailRequest') + '*'}
									</Typography>
									<AppRadioGroup
										control={methods.control}
										name='mailRequest'
										row
										options={YESNO}
									/>
								</Grid>
								<Grid size={6}>
									<Typography variant='body2' color='common.neutral40'>
										{t('member.newsletterConfidential') + '*'}
									</Typography>
									<AppRadioGroup
										control={methods.control}
										name='newsletterConfidential'
										row
										options={YESNO}
									/>
								</Grid>
								<Grid size={6}>
									<AppAutoCompleteMenu
										control={methods.control}
										name='contactMethod'
										label={t('member.contactMethod')}
										options={toEnumOptions(
											allEnumList?.data?.ContactMethodEnum
										)}
									/>
								</Grid>
								<Grid size={6}>
									<AppAutoCompleteMenu
										control={methods.control}
										name='homeVisit'
										label={t('member.homeVisit')}
										options={toEnumOptions(allEnumList?.data?.HomeVisitEnum)}
									/>
								</Grid>
								<Grid size={6}>
									<Typography variant='body2' color='common.neutral40'>
										{t('member.inactiveMember') + '*'}
									</Typography>
									<AppRadioGroup
										control={methods.control}
										name={`inactiveMember`}
										row
										options={YESNOLIST}
									/>
								</Grid>
							</Grid>
							<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
								<Button onClick={handleClose}>{t('button.cancel')}</Button>
								<Button
									type='submit'
									variant='contained'
									endIcon={<CheckCircleOutlineIcon />}
								>
									{t('button.complete')}
								</Button>
							</CardActions>
						</Card>
					</form>
				</FormProvider>
			</Box>
		</>
	);
}
